import type { OdooTask, FaseSysteem } from '@/types/odoo';
import { displayProjectName } from '@/utils/format';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';

interface TaskCardProps {
  task: OdooTask;
  installerNames: Record<number, string>;
  projectNames: Record<number, string>;
  werfAddresses: Record<number, string>;
  faseSystems?: Record<number, FaseSysteem>;
  onClick: () => void;
  showOpgelijst?: boolean;
  showFotos?: boolean;
}

function getProjectleiderRowBg(task: OdooTask): string {
  const plName = task.x_studio_projectleider ? task.x_studio_projectleider[1].toLowerCase() : '';
  if (plName.includes('david')) return 'bg-blue-50';
  if (plName.includes('jorn')) return 'bg-gray-100';
  return '';
}

export function TaskCard({ task, installerNames, projectNames, werfAddresses, faseSystems, onClick, showOpgelijst, showFotos }: TaskCardProps) {
  const soId = task.sale_order_id && Array.isArray(task.sale_order_id) ? task.sale_order_id[0] : null;
  const projectName = (soId && projectNames[soId]) || (task.project_id ? displayProjectName(task.project_id[1]) : '');
  const werfId = Array.isArray(task.x_studio_afleveradres) ? task.x_studio_afleveradres[0] : null;
  const address = werfId ? werfAddresses[werfId] || '' : '';
  const projectleider = task.x_studio_projectleider ? task.x_studio_projectleider[1] : '';
  const instIds: number[] = Array.isArray(task.x_studio_installateur) ? task.x_studio_installateur : [];
  const instNames = instIds.map(id => installerNames[id]).filter(Boolean);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left ${getProjectleiderRowBg(task) || 'bg-white'} rounded-xl shadow-sm border border-gray-100 p-4 active:bg-gray-50 transition`}
    >
      {/* Header: project name (bold) on top, task name below */}
      <div className="flex-1 min-w-0">
        {projectName ? (
          <h3 className="font-semibold text-td-navy text-sm leading-tight truncate">
            {projectName}
          </h3>
        ) : (
          <h3 className="font-semibold text-td-light text-sm leading-tight italic">
            Geen project
          </h3>
        )}
        <p className="text-xs text-td-blue mt-0.5 truncate">
          {task.isInterventie && <InterventieBadge className="mr-1.5 align-middle" />}
          {task.name}
        </p>
      </div>

      {/* Meta row: address + projectleider */}
      <div className="mt-3 flex flex-wrap items-start gap-x-4 gap-y-1 text-xs text-gray-500">
        {address && (
          <div className="flex items-start gap-1 min-w-0">
            <svg
              className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="break-words">{address}</span>
          </div>
        )}
        {projectleider && (
          <div className="flex items-start gap-1 min-w-0">
            <svg
              className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <span className="break-words">{projectleider}</span>
          </div>
        )}
      </div>

      {/* Opgelijst badge for oplijster role */}
      {showOpgelijst && (
        <div className="mt-2">
          {task.x_studio_opgelijst ? (
            <span className="px-2.5 py-1 text-xs font-bold bg-td-green/10 text-td-green rounded-full">
              Opgelijst
            </span>
          ) : (
            <span className="px-2.5 py-1 text-xs font-bold bg-orange-50 text-orange-500 rounded-full">
              Niet opgelijst
            </span>
          )}
        </div>
      )}

      {/* Uitgevoerde m² deze fase pill */}
      {showFotos && (() => {
        const entry = faseSystems?.[task.id];
        if (!entry || entry.totalM2 === 0) return null;
        const { opDrukM2, totalM2 } = entry;
        let colorClasses: string;
        if (opDrukM2 >= totalM2) {
          colorClasses = 'bg-td-green/10 text-td-green';
        } else if (opDrukM2 > 0) {
          colorClasses = 'bg-teal-50 text-teal-600';
        } else {
          colorClasses = 'bg-gray-100 text-td-muted';
        }
        return (
          <div className="mt-2">
            <span className={`px-2.5 py-1 text-xs font-bold ${colorClasses} rounded-full`}>
              {Math.round(opDrukM2)} / {Math.round(totalM2)} m² uitgevoerd
            </span>
          </div>
        );
      })()}

      {/* Footer: installer names */}
      <div className="mt-2 flex items-center gap-2">
        {instNames.length === 0 ? (
          <div className="flex items-center gap-2 text-gray-400">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <span className="text-xs italic">Niet toegewezen</span>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <svg
              className="w-4 h-4 text-gray-500 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            <span className="text-xs text-gray-600 truncate">
              {instNames.join(', ')}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}
